import React from 'react';
import { canAccessQCModule } from '../../utils/roleConstants';
import { Form, FormGroup, FormControl, Button, Modal, Row, Col } from 'react-bootstrap';
import config from '../../../config';
import DataTable from 'react-data-table-component';
import { Navigate } from 'react-router';
import utils from '../../utils';
import moment from 'moment';
import DataTableEllipsisText from '../../_components/DataTableEllipsisText';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import QValuePageHeader from '../../_components/QValuePageHeader';
import { Q_VALUE_PAGE_HEADERS } from '../../utils/qValuePageHeaders';
import { getQValueAccountId } from './qValueAccount';
import {
    DEFECT_SEVERITY_OPTIONS,
    DEFECT_SEVERITY_STYLES,
    normalizeDefectRow,
    serializeDefectCategoryList,
} from './defectCategoryConstants';

const tableCustomStyles = {
    headRow: {
        style: {
            backgroundColor: '#1e3a5f',
            color: '#fff',
            fontWeight: 700,
            minHeight: '48px',
        },
    },
    headCells: {
        style: { color: '#fff', fontSize: '13px' },
    },
};

function SeverityCell({ value }) {
    if (!value) return <span className="text-muted">—</span>;
    const style = DEFECT_SEVERITY_STYLES[value] || {};
    return <span style={style}>{value}</span>;
}

const emptyForm = () => ({
    id: 0,
    processCategorySelect: '',
    processCategoryCustom: '',
    defectCategoryList: [''],
    severity: '',
    physicalIndication: '',
});

class DefectCategory extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            ...emptyForm(),
            keyword: '',
            list: [],
            body: [],
            currentPage: 1,
            rowsPerPage: 10,
            totalRows: 0,
            loading: false,
            modalShow: false,
            newMode: false,
            masterProcesses: [],
        };
        this.tableRequestSeq = 0;
        this.columns = [
            {
                name: 'Process Category',
                selector: (row) => row.processCategory,
                width: '160px',
                cell: (row) => <DataTableEllipsisText value={row.processCategory} />,
            },
            {
                name: 'Defect Category',
                selector: (row) => row.defectCategory,
                grow: 2,
                cell: (row) => <DataTableEllipsisText value={row.defectCategory} />,
            },
            {
                name: 'Severity (Optional)',
                selector: (row) => row.severity,
                width: '140px',
                cell: (row) => <SeverityCell value={row.severity} />,
            },
            {
                name: 'Description / Physical Indication Optional',
                selector: (row) => row.physicalIndication,
                grow: 2,
                cell: (row) => <DataTableEllipsisText value={row.physicalIndication} />,
            },
            {
                name: 'Action',
                cell: (row) => (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => this.onEdit(row)}>Edit</button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => window.confirm('Delete this row?') && this.onDelete(row)}>Delete</button>
                    </div>
                ),
                ignoreRowClick: true,
                width: '150px',
            },
        ];
    }

    componentDidMount() {
        this.loadMasterProcesses();
        this.loadTable();
    }

    loadMasterProcesses() {
        fetch(`${config.apiUrl}/getAllProcesses`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => response.text().then((text) => (text ? JSON.parse(text) : [])))
            .then((processes) => {
                this.setState({ masterProcesses: Array.isArray(processes) ? processes : [] });
            })
            .catch(() => this.setState({ masterProcesses: [] }));
    }

    masterProcessDescriptions() {
        return (this.state.masterProcesses || [])
            .map((p) => (p.description || '').trim())
            .filter(Boolean);
    }

    resolvedProcessCategory(state) {
        const custom = (state.processCategoryCustom || '').trim();
        if (custom) return custom;
        return (state.processCategorySelect || '').trim();
    }

    loadTable(page = null, limit = null) {
        const company = getQValueAccountId();
        if (!company) return;
        const currentPage = page != null ? page : this.state.currentPage;
        const rowsPerPage = limit != null ? limit : this.state.rowsPerPage;
        const offset = (currentPage - 1) * rowsPerPage;
        const requestSeq = ++this.tableRequestSeq;
        this.setState({ loading: true });
        fetch(`${config.apiUrl}/getDefectCategoryListByCompany?company=${company}&keyword=${encodeURIComponent(this.state.keyword || '')}&limit=${rowsPerPage}&offset=${offset}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (requestSeq !== this.tableRequestSeq) return;
                const rows = (body && body.data) || [];
                const list = rows.map((data) => normalizeDefectRow(data));
                this.setState({
                    list,
                    body: rows,
                    totalRows: body.meta && body.meta.total_count ? body.meta.total_count : 0,
                    currentPage,
                    rowsPerPage,
                    loading: false,
                });
            })
            .catch(() => {
                if (requestSeq === this.tableRequestSeq) {
                    this.setState({ list: [], body: [], totalRows: 0, loading: false });
                }
            });
    }

    openNewModal() {
        this.setState({
            modalShow: true,
            newMode: true,
            ...emptyForm(),
        });
    }

    onEdit(row) {
        const record = normalizeDefectRow(this.state.body.find((r) => r.id === row.id) || row);
        const known = this.masterProcessDescriptions();
        const inList = known.includes(record.processCategory);
        this.setState({
            modalShow: true,
            newMode: false,
            id: record.id,
            processCategorySelect: inList ? record.processCategory : '',
            processCategoryCustom: inList ? '' : record.processCategory,
            defectCategoryList: record.defectCategoryList.length
                ? [...record.defectCategoryList]
                : [''],
            severity: record.severity || '',
            physicalIndication: record.physicalIndication,
        });
    }

    updateDefectCategoryItem(index, value) {
        const defectCategoryList = [...this.state.defectCategoryList];
        defectCategoryList[index] = value;
        this.setState({ defectCategoryList });
    }

    addDefectCategoryItem = () => {
        this.setState({ defectCategoryList: [...this.state.defectCategoryList, ''] });
    };

    removeDefectCategoryItem = (index) => {
        const defectCategoryList = this.state.defectCategoryList.filter((_, i) => i !== index);
        this.setState({ defectCategoryList: defectCategoryList.length ? defectCategoryList : [''] });
    };

    onDelete(row) {
        fetch(`${config.apiUrl}/deleteDefectCategory`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: row.id }),
        }).then(() => this.loadTable());
    }

    buildPayload(state) {
        const companyId = getQValueAccountId();
        const processCategory = this.resolvedProcessCategory(state);
        const items = (state.defectCategoryList || []).map((s) => s.trim()).filter(Boolean);
        const defectCategory = serializeDefectCategoryList(items);
        return {
            id: state.id,
            companyId,
            processCategory,
            defectCategory,
            severity: state.severity || null,
            physicalIndication: state.physicalIndication,
            name: processCategory || (items[0] || '').slice(0, 255),
            code: processCategory,
            description: state.physicalIndication,
            status: 'Active',
        };
    }

    hasDefectCategoryInput(state) {
        return (state.defectCategoryList || []).some((item) => String(item).trim());
    }

    handlePageChange = (page) => {
        this.setState({ currentPage: page }, () => this.loadTable(page, this.state.rowsPerPage));
    };

    handleRowsPerPageChange = (newRowsPerPage, page) => {
        this.setState({ rowsPerPage: newRowsPerPage, currentPage: page }, () => this.loadTable(page, newRowsPerPage));
    };

    exportExcel() {
        utils.tableToExcel('defect-category-matrix', 'defect_category_matrix', `Defect_Category_Matrix_${moment().format('YYYYMMDD')}.xls`);
    }

    render() {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!canAccessQCModule(user)) {
            return <Navigate to="/" />;
        }
        const {
            keyword, list, modalShow, newMode,
            processCategorySelect, processCategoryCustom, defectCategoryList, severity, physicalIndication,
            masterProcesses,
        } = this.state;
        const handleClose = () => this.setState({ modalShow: false });
        const canSave = this.resolvedProcessCategory(this.state) && this.hasDefectCategoryInput(this.state);

        const handleSave = () => {
            fetch(`${config.apiUrl}/updateDefectCategory`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.buildPayload(this.state)),
            }).then((response) => {
                if (response.ok) {
                    this.setState({ modalShow: false });
                    this.loadTable();
                }
            });
        };
        const handleCreate = () => {
            fetch(`${config.apiUrl}/uploadDefectCategory`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.buildPayload(this.state)),
            }).then((response) => {
                if (response.ok) {
                    this.setState({ modalShow: false });
                    this.loadTable();
                }
            });
        };

        return (
            <div className="container">
                <QValuePageHeader {...Q_VALUE_PAGE_HEADERS.defect_category} />
                <div id="defect-category-matrix" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
                        <FormControl
                            type="text"
                            placeholder="Search..."
                            value={keyword || ''}
                            onChange={(ev) => this.setState({ keyword: ev.target.value })}
                            style={{ minWidth: 200 }}
                        />
                        <Button variant="primary" onClick={() => this.setState({ currentPage: 1 }, () => this.loadTable(1))}>Search</Button>
                        <Button variant="primary" onClick={() => this.exportExcel()}>Export</Button>
                        <Button variant="danger" style={{ marginLeft: 'auto' }} onClick={() => this.openNewModal()}>New</Button>
                    </div>
                    <DataTable
                        columns={this.columns}
                        data={list}
                        customStyles={tableCustomStyles}
                        progressPending={this.state.loading}
                        progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading defect category matrix" />}
                        pagination
                        paginationServer
                        paginationTotalRows={this.state.totalRows}
                        paginationPerPage={this.state.rowsPerPage}
                        paginationDefaultPage={this.state.currentPage}
                        onChangePage={this.handlePageChange}
                        onChangeRowsPerPage={this.handleRowsPerPageChange}
                        highlightOnHover
                    />
                </div>
                <Modal size="lg" show={modalShow} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>{newMode ? 'New matrix row' : 'Edit matrix row'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <FormGroup as={Row} className="mb-3">
                                <Col sm={4}><Form.Label className="mb-md-0">Process Category</Form.Label></Col>
                                <Col sm={8}>
                                    <FormControl
                                        as="select"
                                        className="mb-2"
                                        value={processCategorySelect || 'select'}
                                        onChange={(ev) => {
                                            const val = ev.target.value;
                                            this.setState({
                                                processCategorySelect: val === 'select' ? '' : val,
                                                processCategoryCustom: val === 'select' ? processCategoryCustom : '',
                                            });
                                        }}
                                    >
                                        <option value="select">select</option>
                                        {(masterProcesses || []).map((proc) => (
                                            <option key={proc.id} value={proc.description}>{proc.description}</option>
                                        ))}
                                    </FormControl>
                                    <FormControl
                                        type="text"
                                        value={processCategoryCustom}
                                        onChange={(ev) => this.setState({
                                            processCategoryCustom: ev.target.value,
                                            processCategorySelect: ev.target.value ? '' : processCategorySelect,
                                        })}
                                        placeholder="Or enter custom process (same as Master Data)"
                                    />
                                    <Form.Text className="text-muted">Options match the Processes list on the Master Data page.</Form.Text>
                                </Col>
                            </FormGroup>
                            <FormGroup as={Row} className="mb-3">
                                <Col sm={4}><Form.Label className="mb-md-0">Defect Category</Form.Label></Col>
                                <Col sm={8}>
                                    {(defectCategoryList || ['']).map((item, index) => (
                                        <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                                            <span style={{ minWidth: 22, color: '#6c757d', fontWeight: 600 }}>{index + 1}.</span>
                                            <FormControl
                                                type="text"
                                                value={item}
                                                onChange={(ev) => this.updateDefectCategoryItem(index, ev.target.value)}
                                                placeholder="Enter defect type"
                                            />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => this.removeDefectCategoryItem(index)}
                                                disabled={defectCategoryList.length <= 1 && !item.trim()}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                    <Button variant="success" size="sm" onClick={this.addDefectCategoryItem}>+ Add defect</Button>
                                    <Form.Text className="text-muted d-block mt-2">Free text — add one or more defect types for this process.</Form.Text>
                                </Col>
                            </FormGroup>
                            <FormGroup as={Row} className="mb-3 align-items-center">
                                <Col sm={4}><Form.Label className="mb-md-0">Severity (Optional)</Form.Label></Col>
                                <Col sm={8}>
                                    <FormControl as="select" value={severity} onChange={(ev) => this.setState({ severity: ev.target.value })}>
                                        {DEFECT_SEVERITY_OPTIONS.map((opt) => (
                                            <option key={opt || 'none'} value={opt}>{opt || '— None —'}</option>
                                        ))}
                                    </FormControl>
                                </Col>
                            </FormGroup>
                            <FormGroup as={Row} className="mb-2">
                                <Col sm={4}><Form.Label className="mb-md-0">Description / Physical Indication Optional</Form.Label></Col>
                                <Col sm={8}>
                                    <FormControl
                                        as="textarea"
                                        rows={3}
                                        value={physicalIndication}
                                        onChange={(ev) => this.setState({ physicalIndication: ev.target.value })}
                                    />
                                </Col>
                            </FormGroup>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                        {newMode ? (
                            <Button variant="primary" onClick={handleCreate} disabled={!canSave}>Create</Button>
                        ) : (
                            <Button variant="primary" onClick={handleSave} disabled={!canSave}>Save</Button>
                        )}
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export { DefectCategory };
